import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { ATTACH_KINDS, BUTTON_SHORT, isTrigger, motorDirs } from "@/rc/attachments";
import { ButtonPicker, KindPicker, Segmented } from "./AttachmentControls";
import { ControllerDiagram } from "./ControllerDiagram";
import { PortPicker } from "./PortsStep";
import { SpinDiagram } from "./SpinDiagram";

const GROUP_OPTIONS = [
  { value: "same", label: "Turn the same way", sub: "both motors spin in the same direction" },
  { value: "opposite", label: "Turn opposite ways", sub: "mirrored motors, e.g. one each side of a lift" },
];
const STOP_OPTIONS = [
  { value: "hold", label: "Hold in place", sub: "stays put, but may cause motors to overheat" },
  { value: "coast", label: "Coast", sub: "motors go loose and can be turned by hand" },
];
const spinWord = (d) => (d === "R" ? "anticlockwise" : "clockwise");

const Section = ({ title, children }) => (
  <div>
    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</p>
    {children}
  </div>
);

const Motion = ({ a, id, onChange }) => {
  const k = ATTACH_KINDS[a.kind];
  const dirs = motorDirs(a);
  return (
    <div className="space-y-3">
      <div className="grid-bg rounded-xl border bg-muted/30 px-4 pb-2 pt-3">
        <p className="text-xs text-muted-foreground" data-testid={`${id}-spin-caption`}>
          Pressing <b className="text-primary">{k.pos}</b> ({BUTTON_SHORT[a.pos]}): {a.ports.map((p, j) => `${p} ${spinWord(dirs[j])}`).join(", ")}
        </p>
        <SpinDiagram a={a} testId={`${id}-spin`} />
      </div>
      {a.ports.length === 2 && <Segmented options={GROUP_OPTIONS} value={a.group} onChange={(group) => onChange({ group })} testId={`${id}-group`} label="How the two motors turn" />}
      <label className="flex w-fit cursor-pointer items-center gap-2.5 text-sm font-medium">
        <Switch checked={a.dir === "R"} onCheckedChange={() => onChange({ dir: a.dir === "F" ? "R" : "F" })} data-testid={`${id}-reverse`} />
        Reverse direction
      </label>
    </div>
  );
};

export const AttachmentCard = ({ a, index, portConflicts, buttonConflicts, onChange, onRemove }) => {
  const id = `rc-att-${index}`;
  const k = ATTACH_KINDS[a.kind];
  const setPort = (j, port) => onChange({ ports: a.ports.map((p, i) => (i === j ? port : p)) });
  return (
    <div className="fade-up rounded-2xl border bg-card p-4 sm:p-5" data-testid={`rc-attachment-${index}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <p className="font-display font-bold" data-testid={`${id}-title`}>{k.name} · {a.ports.length === 2 ? "2-motor group" : "1 motor"}</p>
          <KindPicker value={a.kind} onChange={(kind) => onChange({ kind })} testId={`${id}-kind`} />
        </div>
        <Button variant="ghost" size="sm" className="rounded-full text-muted-foreground hover:text-destructive" onClick={onRemove} data-testid={`${id}-remove`}>
          <Trash2 className="mr-1.5 h-4 w-4" />Remove
        </Button>
      </div>
      <div className="mt-4 grid gap-5 lg:grid-cols-[260px_1fr]">
        <Motion a={a} id={id} onChange={onChange} />
        <div className="min-w-0 space-y-4">
          <Section title={a.ports.length === 2 ? "Motor ports" : "Motor port"}>
            {a.ports.map((p, j) => (
              <PortPicker key={j} m={{ id: `att${index}-m${j}`, label: `${k.name} motor ${j + 1}` }} prefix={a.ports.length === 2 ? `Motor ${j + 1}` : "Port"} value={p} conflict={portConflicts.has(p)} onPick={(port) => setPort(j, port)} />
            ))}
          </Section>
          <Section title="Buttons">
            <div className="grid gap-3 xl:grid-cols-[1fr_150px] xl:items-center">
              <div className="space-y-2">
                <ButtonPicker testId={`${id}-pos`} label={k.pos} tone="pos" value={a.pos} conflicts={buttonConflicts} onPick={(pos) => onChange({ pos })} />
                <ButtonPicker testId={`${id}-neg`} label={k.neg} tone="neg" value={a.neg} conflicts={buttonConflicts} onPick={(neg) => onChange({ neg })} />
              </div>
              <ControllerDiagram marks={{ [a.neg]: "neg", [a.pos]: "pos" }} className="mx-auto hidden w-full max-w-[150px] xl:block" />
            </div>
            {(isTrigger(a.pos) || isTrigger(a.neg)) && <p className="mt-2 text-xs text-muted-foreground">Triggers: press harder to go faster.</p>}
          </Section>
          <Section title="Top speed">
            <div className="flex items-center gap-4">
              <Slider min={10} max={100} step={5} value={[a.speed]} onValueChange={([speed]) => onChange({ speed })} aria-label="Top speed" data-testid={`${id}-speed`} />
              <span className="w-12 text-right font-display font-bold text-primary" data-testid={`${id}-speed-value`}>{a.speed}%</span>
            </div>
          </Section>
          <Section title="When you let go">
            <Segmented options={STOP_OPTIONS} value={a.stop} onChange={(stop) => onChange({ stop })} testId={`${id}-stop`} label="Stopping mode" />
          </Section>
        </div>
      </div>
    </div>
  );
};
